import React, { useState, useEffect, useCallback } from "react";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import ModalVisaApplicantForm from "../Visas/VisaApplicantForm";
import { toast } from "react-toastify";
import {
  RiUserSearchLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiPassportLine,
  RiFolderUserLine,
  RiAlertLine,
  RiVisaLine,
  RiSendPlaneLine,
  RiUserAddLine,
} from "react-icons/ri";
import "./VisaManageApplicants.css";

const VisaManageApplicants = ({
  visaRequestId,
  visaRequest,
  onApplicantsChange,
}) => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [expandedAll, setExpandedAll] = useState(false);

  // New states for supplier functionality
  const [showNoApplicantsModal, setShowNoApplicantsModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [sendingToSupplier, setSendingToSupplier] = useState(false);

  // State for applicant form modal
  const [showApplicantForm, setShowApplicantForm] = useState(false);

  const isSubmitted =
    visaRequest?.visaRequestStatus?.toUpperCase() === "SUBMITTED";

  const GetData = useCallback(async () => {
    if (!visaRequestId) return;
    setLoading(true);
    try {
      const response = await apiService.get(
        `${config.GETVISAAPPLICANTSBYREQUEST}/${visaRequestId}`
      );
      const allApplicants = response || [];
      setApplicants(allApplicants);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      setLoading(false);
    }
  }, [visaRequestId]);

  useEffect(() => {
    GetData();
  }, [visaRequestId, GetData]);

  // New function to handle send to supplier
  const handleSendToSupplier = async () => {
    if (!visaRequestId || !visaRequest?.supplierId) {
      toast.error("No supplier assigned to this visa request");
      return;
    }

    if (applicants.length === 0) {
      setShowNoApplicantsModal(true);
      return;
    }

    setShowConfirmationModal(true);
  };

  const handleConfirmSendToSupplier = async () => {
    if (!visaRequestId || !visaRequest?.supplierId) {
      toast.error("No supplier assigned to this visa request");
      setShowConfirmationModal(false);
      return;
    }

    setSendingToSupplier(true);
    try {
      const url = `${config.SENDVISATOSUPPLIER}?visaRequestId=${visaRequestId}&supplierId=${visaRequest.supplierId}`;
      await apiService.post(url);

      toast.success("Visa request sent to supplier successfully!");
      setShowConfirmationModal(false);

      // Refresh data if callback exists
      if (onApplicantsChange) {
        onApplicantsChange();
      }
    } catch (error) {
      console.error("Error sending visa request to supplier:", error);
      toast.error("Failed to send visa request to supplier");
    } finally {
      setSendingToSupplier(false);
    }
  };

  const handleCloseNoApplicantsModal = () => {
    setShowNoApplicantsModal(false);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  // Function to open applicant form
  const handleOpenApplicantForm = () => {
    setShowApplicantForm(true);
    setShowNoApplicantsModal(false);
  };

  const handleCloseApplicantForm = () => {
    setShowApplicantForm(false);
    // Refresh applicants data when form closes
    GetData();
  };

  const handleApplicantFormSuccess = () => {
    // Refresh data when applicant is added/updated
    GetData();
    if (onApplicantsChange) {
      onApplicantsChange();
    }
  };

  const toggleDetails = (id) => {
    setExpandedDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleExpandAll = () => {
    if (expandedAll) {
      // Collapse all
      setExpandedDetails({});
    } else {
      // Expand all
      const allExpanded = {};
      applicants.forEach((app) => {
        allExpanded[app.visaApplicantId] = true;
      });
      setExpandedDetails(allExpanded);
    }
    setExpandedAll(!expandedAll);
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return (
          <RiCheckboxCircleLine
            style={{ color: "#10b981", marginRight: "4px" }}
          />
        );
      case "REJECTED":
        return (
          <RiCloseCircleLine style={{ color: "#ef4444", marginRight: "4px" }} />
        );
      default:
        return <RiTimeLine style={{ color: "#f59e0b", marginRight: "4px" }} />;
    }
  };

  const getStatusStyle = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return {
          backgroundColor: "#ecfdf5",
          color: "#10b981",
          padding: "4px 12px",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        };
      case "REJECTED":
        return {
          backgroundColor: "#fef2f2",
          color: "#ef4444",
          padding: "4px 12px",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        };
      case "PENDING":
        return {
          backgroundColor: "#fffbeb",
          color: "#f59e0b",
          padding: "4px 12px",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        };
      case "SUBMITTED":
        return {
          backgroundColor: "#eff6ff",
          color: "#3b82f6",
          padding: "4px 12px",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        };
      default:
        return {
          backgroundColor: "#f3f4f6",
          color: "#6b7280",
          padding: "4px 12px",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        };
    }
  };

  const renderApplicantDetails = (applicant) => (
    <div className="visa-applicant-details">
      <div className="visa-images-container">
        <div className="visa-image-section">
          <div className="visa-image-label">
            <RiUserSearchLine style={{ marginRight: "4px" }} /> Profile Photo
          </div>
          <div className="visa-profile-image">
            <img
              src={applicant.profileSrc || "/assets/images/avatar.png"}
              alt="Profile"
            />
          </div>
        </div>

        <div className="visa-image-section">
          <div className="visa-image-label">
            <RiPassportLine style={{ marginRight: "4px" }} /> Passport Front
          </div>
          <div className="visa-passport-image">
            <img
              src={applicant.passportFrontSrc || "/assets/images/doc_bgc.png"}
              alt="Passport Front"
            />
          </div>
        </div>

        <div className="visa-image-section">
          <div className="visa-image-label">
            <RiPassportLine style={{ marginRight: "4px" }} /> Passport Back
          </div>
          <div className="visa-passport-image">
            <img
              src={applicant.passportBackSrc || "/assets/images/doc_bgc.png"}
              alt="Passport Back"
            />
          </div>
        </div>
      </div>

      <div className="visa-details-grid">
        <div className="visa-detail-section">
          <div className="visa-section-header">
            <RiFolderUserLine style={{ marginRight: "8px" }} />
            <h3 className="visa-section-title">Personal Information</h3>
          </div>

          <div className="visa-details-fields">
            <div className="visa-field">
              <div className="visa-field-label">Name</div>
              <div className="visa-name-title-combined">
                <div className="visa-field-value">
                  {applicant.firstName} {applicant.lastName}
                </div>
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Gender</div>
              <div className="visa-field-value">{applicant.gender}</div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Date of Birth</div>
              <div className="visa-field-value">
                {new Date(applicant.dateOfBirth).toLocaleDateString()}
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Nationality</div>
              <div className="visa-field-value">{applicant.nationality}</div>
            </div>
          </div>
        </div>

        <div className="visa-detail-section">
          <div className="visa-section-header">
            <RiVisaLine style={{ marginRight: "8px" }} />
            <h3 className="visa-section-title">Passport Details</h3>
          </div>

          <div className="visa-details-fields">
            <div className="visa-field">
              <div className="visa-field-label">Passport No.</div>
              <div className="visa-field-value">{applicant.passportNumber}</div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Country</div>
              <div className="visa-field-value">
                {applicant.passportCountry}
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Issue Date</div>
              <div className="visa-field-value">
                {new Date(applicant.passportIssueDate).toLocaleDateString()}
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Expiry Date</div>
              <div className="visa-field-value">
                {new Date(applicant.passportExpireDate).toLocaleDateString()}
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Traveler Type</div>
              <div className="visa-field-value">
                <span className="visa-traveler-type-badge">
                  {applicant.travelerType}
                </span>
              </div>
            </div>

            <div className="visa-field">
              <div className="visa-field-label">Status</div>
              <div style={getStatusStyle(applicant.visaApplicantStatus)}>
                {getStatusIcon(applicant.visaApplicantStatus)}
                {applicant.visaApplicantStatus}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="visa-documents-comments-container">
        <div className="visa-documents-section">
          <div className="visa-documents-header">
            <RiFileTextLine style={{ marginRight: "8px" }} />
            <h3 className="visa-section-title">Additional Documents</h3>
            <span className="visa-documents-count">
              {applicant.documents?.length || 0}
            </span>
          </div>

          {applicant.documents?.length > 0 ? (
            <div className="visa-documents-grid">
              {applicant.documents.map((doc, index) => (
                <div key={index} className="visa-document-item">
                  <RiFileTextLine
                    size={24}
                    style={{ color: "#3b82f6", marginBottom: "8px" }}
                  />
                  <a
                    href={doc.imageSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="visa-document-link"
                  >
                    View <RiArrowUpSLine style={{ marginLeft: "4px" }} />
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="visa-no-documents">
              <RiFileTextLine
                size={32}
                style={{ marginBottom: "8px", opacity: 0.5 }}
              />
              <p>No additional documents uploaded.</p>
            </div>
          )}
        </div>

        {applicant.comments && (
          <div className="visa-comments-section">
            <div className="visa-comments-header">
              <RiAlertLine style={{ marginRight: "8px" }} />
              <h3 className="visa-section-title">Comments</h3>
            </div>
            <p className="visa-comment-text">{applicant.comments}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="visa-applicants-container">
      <div className="visa-applicants-header">
        {applicants.length > 0 && (
          <div className="visa-applicants-count">
            <h2>Applicants</h2>
            <span>{applicants.length}</span>
          </div>
        )}

        <div className="visa-buttons-container">
          {/* Add Applicants Button - Always visible */}
          <button
            onClick={handleOpenApplicantForm}
            className="visa-add-applicants-button"
          >
            <RiUserAddLine style={{ marginRight: "8px" }} />
            Add Applicants
          </button>

          {/* Send to Supplier Button - Only show when there are applicants */}
          {applicants.length > 0 && (
            <button
              onClick={handleSendToSupplier}
              className="visa-send-supplier-button"
              disabled={!visaRequest?.supplierId}
            >
              <RiSendPlaneLine style={{ marginRight: "8px" }} />
              Send to Supplier
              {!visaRequest?.supplierId && " (No Supplier Assigned)"}
            </button>
          )}

          {applicants.length > 0 && (
            <button
              onClick={toggleExpandAll}
              className={`visa-toggle-button ${
                expandedAll ? "visa-expanded" : ""
              }`}
            >
              {expandedAll ? (
                <>
                  <RiArrowUpSLine style={{ marginRight: "8px" }} /> Collapse All
                </>
              ) : (
                <>
                  <RiArrowDownSLine style={{ marginRight: "8px" }} /> Expand All
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="visa-loading-container">
          <div className="visa-spinner"></div>
          <p>Loading applicants...</p>
        </div>
      ) : applicants.length === 0 ? (
        <div className="visa-empty-state">
          <RiUserSearchLine
            size={48}
            style={{ color: "#d1d5db", marginBottom: "16px" }}
          />
          <h3>No Applicants Added Yet</h3>
          <p className="text-muted mb-3">
            Please add applicants to proceed with sending to supplier.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {applicants.map((app) => (
            <div key={app.visaApplicantId} className="visa-applicant-card">
              <div className="visa-applicant-summary">
                <div className="visa-applicant-header">
                  <div className="visa-applicant-info">
                    <div className="visa-applicant-avatar">
                      <img
                        src={app.profileSrc || "/assets/images/avatar.png"}
                        alt={app.firstName}
                      />
                    </div>
                    <div>
                      <h2 className="visa-applicant-name">
                        {app.firstName} {app.lastName}
                      </h2>
                      <div className="visa-applicant-meta">
                        <span className="visa-meta-item">
                          <RiPassportLine style={{ marginRight: "4px" }} />
                          {app.passportNumber}
                        </span>
                        <span className="visa-dot-separator"></span>
                        <span>{app.travelerType}</span>
                        <span className="visa-dot-separator"></span>
                        <div style={getStatusStyle(app.visaApplicantStatus)}>
                          {getStatusIcon(app.visaApplicantStatus)}
                          {app.visaApplicantStatus}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="visa-applicant-actions">
                    <button
                      className="visa-action-button"
                      onClick={() => toggleDetails(app.visaApplicantId)}
                    >
                      {expandedDetails[app.visaApplicantId] ? (
                        <RiArrowUpSLine />
                      ) : (
                        <RiArrowDownSLine />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {expandedDetails[app.visaApplicantId] &&
                renderApplicantDetails(app)}
            </div>
          ))}
        </div>
      )}

      {/* No Applicants Modal */}
      {showNoApplicantsModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-md" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Applicants</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseNoApplicantsModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="alert alert-warning">
                  <i className="ri-error-warning-line fs-2 text-warning"></i>
                  <p className="mt-2 mb-0">
                    Applicants are not added. Please add applicants first.
                  </p>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  className="btn btn-primary"
                  onClick={handleOpenApplicantForm}
                >
                  <RiUserAddLine className="me-1" />
                  Add Applicants
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCloseNoApplicantsModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send to Supplier</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseConfirmationModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <p>
                    Please confirm you want to send this visa request to the
                    supplier:
                  </p>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Visa Type:</strong>
                  </div>
                  <div className="col-md-6">{visaRequest?.visaName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Country:</strong>
                  </div>
                  <div className="col-md-6">{visaRequest?.countryName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Applicants:</strong>
                  </div>
                  <div className="col-md-6">{applicants.length}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Supplier:</strong>
                  </div>
                  <div className="col-md-6">{visaRequest?.supplierName}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCloseConfirmationModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmSendToSupplier}
                  disabled={sendingToSupplier}
                >
                  {sendingToSupplier ? "Sending..." : "Yes Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Applicant Form Modal */}
      {showApplicantForm && (
        <ModalVisaApplicantForm
          show={showApplicantForm}
          onHide={handleCloseApplicantForm}
          visaRequestId={visaRequestId}
          onSuccess={handleApplicantFormSuccess}
          applicantsList={applicants}
        />
      )}
    </div>
  );
};

export default VisaManageApplicants;
